import React, { useEffect, useMemo, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import {
  Container,
  CardBody,
  Row,
  Col,
  Card,
  Form,
  Input,
  Label,
  FormFeedback,
  CardHeader,
  CardFooter,
  Alert,
  Button,
} from "reactstrap";

import { useNavigate, useParams } from "react-router-dom";

import AppFlatpickr from "components/common/AppFlatpickr";
import { useFormik, FormikProvider } from "formik";
import schema from "./schema";
import {
  createQuotation,
  getQuotation,
  updateQuotation,
} from "features/Quotations/services/QuotationService";
import { toast } from "react-toastify";
import type { QuotationItemFormRow } from "features/Quotations/utils/quotationItem.utils";
import { Quotation } from "features/Quotations/types/quotation.types";
import { BackButton } from "components/common/Buttons";
import moment from "moment";
import { getEnquiryOptions } from "features/Enquiries/services/EnquiryService";
import type { Enquiry } from "features/Enquiries/types/enquiry.types";
import {
  buildEnquirySelectDefaultOption,
  enrichEnquirySelectOptionData,
  EnquiryOption,
  EnquirySingleValue,
} from "features/Enquiries/components/EnquirySelectComponents";
import { EnquiryIntent } from "features/Enquiries/constants/enquiry.constants";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import {
  QuotationItemType,
  QuotationSourceType,
  QuotationStatusMap,
} from "features/Quotations/constants/quotation.constants";
import StatusBadge from "components/common/StatusBadge";
import { getClientOptions } from "features/Clients/services/ClientService";
import { getDocumentTitle } from "utils/helpers";
import BoqItemsFieldArray from "components/common/BoqItemsFieldArray";
import {
  formRowsToWritePayload,
  mapQuotationItemToFormRow,
} from "features/Quotations/utils/quotationItem.utils";

function optionalIntFormValue(
  record: Quotation | null,
  camelKey: keyof Quotation,
  snakeKey: string,
): number | "" {
  if (!record) return "";
  const row = record as unknown as Record<string, unknown>;
  const raw = row[camelKey as string] ?? row[snakeKey];
  if (raw == null || raw === "") return "";
  const n = Number(raw);
  return Number.isFinite(n) ? Math.trunc(n) : "";
}

function optionalIntPayload(v: unknown): number | null {
  if (v === "" || v == null || v === undefined) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
}

const emptyBoqRow = (): QuotationItemFormRow => ({
  type: QuotationItemType.SERVICE,
  description: "",
  productId: "",
  quantity: 1,
  unit: "",
  rate: 0,
});

const QuotationForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  document.title = getDocumentTitle(
    isEdit ? "Edit Quotation" : "Create Quotation",
  );

  const navigate = useNavigate();
  const { id } = useParams();
  const [record, setRecord] = useState<Quotation | null>(null);
  const [selectedEnquiryIntent, setSelectedEnquiryIntent] = useState<string | null>(null);

  const linkedEnquiryForSelect = useMemo((): Enquiry | null => {
    if (record?.sourceType !== QuotationSourceType.ENQUIRY) return null;
    const s = record.source;
    if (s && typeof s === "object" && "enquiryIntent" in s) {
      return s as Enquiry;
    }
    return null;
  }, [record]);

  const enquirySelectComponents = useMemo(
    () => ({
      Option: EnquiryOption,
      SingleValue: (p: any) => (
        <EnquirySingleValue
          {...p}
          data={enrichEnquirySelectOptionData(p.data, linkedEnquiryForSelect)}
        />
      ),
    }),
    [linkedEnquiryForSelect],
  );

  useEffect(() => {
    setSelectedEnquiryIntent(linkedEnquiryForSelect?.enquiryIntent ?? null);
  }, [linkedEnquiryForSelect]);

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      sourceType: record?.sourceType ?? QuotationSourceType.ENQUIRY,
      sourceId: record?.sourceId ?? "",
      clientId: record?.clientId ?? "",
      title: record?.title ?? "",
      validUntil: record?.validUntil
        ? new Date(record.validUntil).toISOString().slice(0, 10)
        : "",
      estimatedStartDate: record?.estimatedStartDate
        ? new Date(record.estimatedStartDate).toISOString().slice(0, 10)
        : "",
      estimatedEndDate: record?.estimatedEndDate
        ? new Date(record.estimatedEndDate).toISOString().slice(0, 10)
        : "",
      description: record?.description ?? "",
      advancePaymentPercent: optionalIntFormValue(
        record,
        "advancePaymentPercent",
        "advance_payment_percent",
      ),
      workCompletionWeeks: optionalIntFormValue(
        record,
        "workCompletionWeeks",
        "work_completion_weeks",
      ),
      items: record?.items?.length
        ? record.items.map(mapQuotationItemToFormRow)
        : [emptyBoqRow()],
      isEdit,
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const clientId = Number(values.clientId);
      const sourceId = Number(values.sourceId);
      const sourceType = values.sourceType as QuotationSourceType;
      const items = formRowsToWritePayload(
        values.items as QuotationItemFormRow[],
      );
      try {
        let response;
        const advancePaymentPercent = optionalIntPayload(
          values.advancePaymentPercent,
        );
        const workCompletionWeeks = optionalIntPayload(
          values.workCompletionWeeks,
        );

        if (isEdit) {
          response = await updateQuotation(Number(id), {
            title: values.title,
            validUntil: values.validUntil,
            estimatedStartDate: values.estimatedStartDate,
            estimatedEndDate: values.estimatedEndDate,
            description: values.description || null,
            advancePaymentPercent,
            workCompletionWeeks,
            clientId,
            items,
          });
        } else {
          response = await createQuotation({
            title: values.title,
            sourceId,
            sourceType,
            clientId,
            validUntil: values.validUntil,
            estimatedStartDate: values.estimatedStartDate,
            estimatedEndDate: values.estimatedEndDate,
            description: values.description || null,
            advancePaymentPercent,
            workCompletionWeeks,
            items,
          });
        }

        if (response.success) {
          toast.success(response.message);
          navigate(-1);
        }
      } catch (err) {
        toast.error((err as any).response.data.message);
        console.log(err);
      }
    },
  });

  useEffect(() => {
    const fetchRecord = async () => {
      const quotationId = Number(id);

      if (!Number.isFinite(quotationId)) {
        return;
      }

      try {
        const response = await getQuotation(quotationId);
        if (response.success) {
          setRecord(response.data);
          console.log(response.message);
        }
      } catch (err) {
        console.warn(err);
      }
    };

    fetchRecord();
  }, [id]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={isEdit ? "Edit Quotation" : "Create Quotation"}
            pageTitle="Quotations"
            items={[{ label: "Quotations", path: "/quotations" }]}
          />
          <FormikProvider value={validation}>
            <Form
              className="needs-validation"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <Row className="justify-content-center">
                <Col>
                  <Card className="shadow-none border">
                    <CardHeader className="border-bottom bg-transparent pb-3">
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        <h5 className="card-title mb-0">
                          {isEdit ? "Edit quotation" : "Create quotation"}
                        </h5>
                        {record ? (
                          <StatusBadge
                            value={record.status}
                            map={QuotationStatusMap}
                          />
                        ) : null}
                      </div>
                      <p className="text-muted mb-0 small">
                        Give this quotation a title and notes, link the source
                        enquiry, set the client and validity, then add line
                        items.
                      </p>
                    </CardHeader>
                    <CardBody className="pt-4">
                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Details
                      </div>
                      <Row className="g-3 mb-2">
                        <Col md={12}>
                          <Label className="form-label">Title</Label>
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Enter quotation title"
                            name="title"
                            onChange={validation.handleChange}
                            value={validation.values.title}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.title &&
                              validation.errors.title
                                ? true
                                : false
                            }
                          />
                          {validation.touched.title &&
                          validation.errors.title ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.title}</div>
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col md={12}>
                          <Label className="form-label">Notes</Label>
                          <Input
                            type="textarea"
                            className="form-control"
                            placeholder="Enter notes"
                            rows={3}
                            name="description"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.description || ""}
                            invalid={
                              validation.touched.description &&
                              validation.errors.description
                                ? true
                                : false
                            }
                          />
                          {validation.touched.description &&
                          validation.errors.description ? (
                            <FormFeedback type="invalid">
                              {validation.errors.description}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>

                      <hr className="my-4 text-muted opacity-25" />

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Source
                      </div>
                      <Row className="mb-2">
                        <Col md={6}>
                          <Label className="form-label">Enquiry</Label>
                          <AsyncPaginatedSelect
                            key="enquiry-select"
                            components={enquirySelectComponents}
                            defaultOption={buildEnquirySelectDefaultOption(
                              linkedEnquiryForSelect,
                            )}
                            fetchOptions={getEnquiryOptions}
                            placeholder="Search Enquiries..."
                            value={validation.values.sourceId}
                            onChange={(val: any) => {
                              validation.setFieldTouched("sourceId", true);
                              validation.setFieldValue(
                                "sourceId",
                                val?.value,
                              );
                              setSelectedEnquiryIntent(
                                val?.enquiryIntent ?? null,
                              );
                            }}
                            onBlur={() =>
                              validation.setFieldTouched("sourceId", true)
                            }
                            isInvalid={
                              validation.touched.sourceId &&
                              validation.errors.sourceId
                            }
                            isDisabled={isEdit}
                          />
                          {(validation.touched.sourceId ||
                            validation.submitCount > 0) &&
                          validation.errors.sourceId ? (
                            <FormFeedback type="invalid" className="d-block">
                              {validation.errors.sourceId}
                            </FormFeedback>
                          ) : null}

                          {selectedEnquiryIntent === EnquiryIntent.AMC ||
                          selectedEnquiryIntent === EnquiryIntent.SERVICE ? (
                            <Alert color="info" className="mb-0 mt-3 py-2 px-3">
                              <i className="ri-information-line align-middle me-1" />
                              {selectedEnquiryIntent === EnquiryIntent.AMC
                                ? "Once this quotation is approved, it will be converted to an AMC."
                                : "Once this quotation is approved, it will be converted to a work order."}
                            </Alert>
                          ) : null}
                        </Col>
                      </Row>

                      <hr className="my-4 text-muted opacity-25" />

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Client
                      </div>
                      <Row className="g-3 mb-2">
                        <Col md={6}>
                          <Label className="form-label">Client</Label>
                          <AsyncPaginatedSelect
                            defaultOption={
                              record?.client && {
                                value: record.client.id,
                                label:
                                  record.client.name?.trim() ||
                                  record.client.company?.trim() ||
                                  `Client #${record.client.id}`,
                              }
                            }
                            fetchOptions={getClientOptions}
                            placeholder="Search Clients..."
                            value={validation.values.clientId}
                            onChange={(val: any) =>
                              validation.setFieldValue("clientId", val?.value)
                            }
                            onBlur={() =>
                              validation.setFieldTouched("clientId", true)
                            }
                            isInvalid={
                              validation.touched.clientId &&
                              validation.errors.clientId
                            }
                          />
                          {validation.touched.clientId &&
                          validation.errors.clientId ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.clientId}</div>
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>

                      <hr className="my-4 text-muted opacity-25" />

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Validity
                      </div>
                      <Row className="g-3 mb-2">
                        <Col md={6}>
                          <Label className="text-muted mb-2">Valid till</Label>
                          <AppFlatpickr
                            name="validUntil"
                            className={`form-control ${
                              validation.errors.validUntil &&
                              validation.touched.validUntil
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Select valid till date"
                            options={{
                              altInput: true,
                              altFormat: "d M, Y",
                              dateFormat: "d M, Y",
                              minDate: moment()
                                .add(1, "day")
                                .startOf("day")
                                .toDate(),
                            }}
                            onChange={(date: any) =>
                              validation.setFieldValue(
                                "validUntil",
                                moment(date[0]).format("DD MMMM ,YYYY"),
                              )
                            }
                            value={
                              validation.values.validUntil
                                ? new Date(validation.values.validUntil)
                                : ""
                            }
                          />
                          {validation.touched.validUntil &&
                          validation.errors.validUntil ? (
                            <FormFeedback type="invalid">
                              {validation.errors.validUntil}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>

                      <hr className="my-4 text-muted opacity-25" />

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Estimated timeline
                      </div>
                      <p className="text-muted small mb-3">
                        A rough idea of when the work is expected to start and
                        end — not a firm schedule.
                      </p>
                      <Row className="g-3 mb-2">
                        <Col md={6}>
                          <Label className="text-muted mb-2">
                            Estimated start date
                          </Label>
                          <AppFlatpickr
                            name="estimatedStartDate"
                            className={`form-control ${
                              validation.errors.estimatedStartDate &&
                              validation.touched.estimatedStartDate
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Select start date"
                            options={{
                              altInput: true,
                              altFormat: "d M, Y",
                              dateFormat: "d M, Y",
                            }}
                            onChange={(date: any) =>
                              validation.setFieldValue(
                                "estimatedStartDate",
                                moment(date[0]).format("DD MMMM ,YYYY"),
                              )
                            }
                            value={
                              validation.values.estimatedStartDate
                                ? new Date(
                                    validation.values.estimatedStartDate,
                                  )
                                : ""
                            }
                          />
                          {validation.touched.estimatedStartDate &&
                          validation.errors.estimatedStartDate ? (
                            <FormFeedback type="invalid">
                              {validation.errors.estimatedStartDate}
                            </FormFeedback>
                          ) : null}
                        </Col>

                        <Col md={6}>
                          <Label className="text-muted mb-2">
                            Estimated end date
                          </Label>
                          <AppFlatpickr
                            name="estimatedEndDate"
                            className={`form-control ${
                              validation.errors.estimatedEndDate &&
                              validation.touched.estimatedEndDate
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Select end date"
                            options={{
                              altInput: true,
                              altFormat: "d M, Y",
                              dateFormat: "d M, Y",
                            }}
                            onChange={(date: any) =>
                              validation.setFieldValue(
                                "estimatedEndDate",
                                moment(date[0]).format("DD MMMM ,YYYY"),
                              )
                            }
                            value={
                              validation.values.estimatedEndDate
                                ? new Date(validation.values.estimatedEndDate)
                                : ""
                            }
                          />
                          {validation.touched.estimatedEndDate &&
                          validation.errors.estimatedEndDate ? (
                            <FormFeedback type="invalid">
                              {validation.errors.estimatedEndDate}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>

                      {/*
                      <hr className="my-4 text-muted opacity-25" />

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        PDF offer terms
                      </div>
                      <p className="text-muted small mb-3">
                        Standard wording on the generated quotation PDF (payment
                        schedule and completion window). Optional — placeholders
                        are used when left blank.
                      </p>
                      <Row className="g-3 mb-2">
                        <Col md={6}>
                          <Label className="form-label" htmlFor="quotation-advance-pct">
                            Advance payment (%)
                          </Label>
                          <Input
                            id="quotation-advance-pct"
                            type="number"
                            name="advancePaymentPercent"
                            className="form-control"
                            placeholder="e.g. 30"
                            min={0}
                            max={100}
                            step={1}
                            value={
                              validation.values.advancePaymentPercent === ""
                                ? ""
                                : validation.values.advancePaymentPercent
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              !!(
                                validation.touched.advancePaymentPercent &&
                                validation.errors.advancePaymentPercent
                              )
                            }
                          />
                          <small className="text-muted">
                            First payment percentage in PDF terms.
                          </small>
                          {validation.touched.advancePaymentPercent &&
                          validation.errors.advancePaymentPercent ? (
                            <FormFeedback type="invalid" className="d-block">
                              {String(validation.errors.advancePaymentPercent)}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col md={6}>
                          <Label className="form-label" htmlFor="quotation-work-weeks">
                            Work completion (weeks)
                          </Label>
                          <Input
                            id="quotation-work-weeks"
                            type="number"
                            name="workCompletionWeeks"
                            className="form-control"
                            placeholder="e.g. 8"
                            min={1}
                            max={999}
                            step={1}
                            value={
                              validation.values.workCompletionWeeks === ""
                                ? ""
                                : validation.values.workCompletionWeeks
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              !!(
                                validation.touched.workCompletionWeeks &&
                                validation.errors.workCompletionWeeks
                              )
                            }
                          />
                          <small className="text-muted">
                            Timeline line in the PDF (weeks from start).
                          </small>
                          {validation.touched.workCompletionWeeks &&
                          validation.errors.workCompletionWeeks ? (
                            <FormFeedback type="invalid" className="d-block">
                              {String(validation.errors.workCompletionWeeks)}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>
                      */}

                      <hr className="my-4 text-muted opacity-25" />

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-2">
                        Bill of quantities
                      </div>
                      <p className="text-muted small mb-3">
                        Materials, labour, and services — description, unit,
                        quantity, and rate (amount is quantity × rate).
                      </p>
                      <div className="border rounded p-3 bg-light bg-opacity-25">
                        <BoqItemsFieldArray validation={validation} embedded />
                      </div>
                    </CardBody>
                    <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                      <BackButton />
                      <Button
                        type="submit"
                        color="primary"
                        className="btn-label px-4"
                        disabled={validation.isSubmitting}
                      >
                        {validation.isSubmitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            />
                            {isEdit ? "Updating…" : "Submitting…"}
                          </>
                        ) : isEdit ? (
                          "Save changes"
                        ) : (
                          "Create quotation"
                        )}
                      </Button>
                    </CardFooter>
                  </Card>
                </Col>
              </Row>
            </Form>
          </FormikProvider>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default QuotationForm;
